import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaTrash, FaUserPlus, FaUsers, FaUpload, FaImage, FaCogs, FaKey, FaCalendarAlt, FaPaperPlane } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL;

interface SettingType {
  IGST: number;
  CGST: number;
  SGST: number;
  InvoiceSN: string;
  logo?: string | null;
  selected_fys?: string | null;
  sms_api_url?: string | null;
  sms_auth_key?: string | null;
  sms_sender_id?: string | null;
  smtp_host?: string | null;
  smtp_port?: number | null;
  smtp_user?: string | null;
  smtp_pass?: string | null;
  smtp_secure?: boolean | number | null;
  smtp_from_email?: string | null;
  wa_api_url?: string | null;
  wa_token?: string | null;
  wa_phone_number_id?: string | null;
  wa_business_account_id?: string | null;
  gemini_api_key?: string | null;
}

interface UserType {
  id: number;
  name: string;
  email: string;
  username: string;
  company_id?: number | null;
  mapped_company_name?: string | null;
  subscription_plan_id?: number | string | null;
  subscription_start_date?: string | null;
  subscription_status?: string | null;
  subscription_plan_name?: string | null;
}

function decodeToken(token: any) {
  if (!token) return {};
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return {};
  }
}

const Settings: React.FC = () => {
  const [activeSubTab, setActiveSubTab] = useState<'tax' | 'logo' | 'users' | 'fy' | 'apis' | 'subscriptions' | 'security'>('tax');
  const [subscriptionPlans, setSubscriptionPlans] = useState<any[]>([]);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [planForm, setPlanForm] = useState({ name: '', price: '', duration_months: '1', max_invoices: '100', max_users: '10' });
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loggedUserId, setLoggedUserId] = useState<number | null>(null);
  const [newSecPassword, setNewSecPassword] = useState('');
  const [confirmSecPassword, setConfirmSecPassword] = useState('');
  const [showSecPassword, setShowSecPassword] = useState(false);
  
  // User Subscription States
  const [showUserSubModal, setShowUserSubModal] = useState(false);
  const [selectedUserForSub, setSelectedUserForSub] = useState<any>(null);
  const [userSubForm, setUserSubForm] = useState({ subscription_plan_id: '', subscription_start_date: '', subscription_status: 'active' });

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = decodeToken(token);
      const initialRole = decoded.role || null;
      setUserRole(initialRole);
      const userId = decoded.userId || decoded.id;
      if (userId) {
        setLoggedUserId(Number(userId));
      }

      // Verify actual user role from database
      if (userId && initialRole !== 'company') {
        axios.get(`${API_URL}/auth/allusersinfo`)
          .then(res => {
            const userFound = (Array.isArray(res.data) ? res.data : []).find((u: any) => String(u.id) === String(userId));
            if (userFound && userFound.role) {
              setUserRole(userFound.role);
            }
          })
          .catch(err => console.error("Error verifying user role in Settings:", err));
      }
    }
  }, []);

  const fetchSubscriptionPlans = () => {
    axios
      .get(`${API_URL}/auth/subscription-plans`)
      .then((res) => {
        setSubscriptionPlans(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        setSubscriptionPlans([]);
      });
  };

  useEffect(() => {
    if (userRole === 'super-admin') {
      fetchSubscriptionPlans();
    }
  }, [userRole]);

  const [setting, setSetting] = useState<SettingType>({
    IGST: 0,
    CGST: 0,
    SGST: 0,
    InvoiceSN: '',
    logo: null,
    selected_fys: null,
    sms_api_url: '',
    sms_auth_key: '',
    sms_sender_id: '',
    smtp_host: '',
    smtp_port: null,
    smtp_user: '',
    smtp_pass: '',
    smtp_secure: false,
    smtp_from_email: '',
    wa_api_url: '',
    wa_token: '',
    wa_phone_number_id: '',
    wa_business_account_id: '',
    gemini_api_key: '',
  });
  
  // User lists & Form state
  const [users, setUsers] = useState<UserType[]>([]);
  const [newUser, setNewUser] = useState({ username: '', email: '', password: '', company_id: '', subscription_plan_id: '', subscription_start_date: '', subscription_status: 'active' });
  const [companies, setCompanies] = useState<any[]>([]);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [selectedFys, setSelectedFys] = useState<string[]>([]);
  
  // Passwords visibility and edit state
  const [showCreatePass, setShowCreatePass] = useState(false);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [newPasswordVal, setNewPasswordVal] = useState<string>("");
  const [showEditPass, setShowEditPass] = useState<boolean>(false);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch settings from the server
  const fetchSettings = () => {
    setLoading(true);
    setError('');
    axios
      .get(`${API_URL}/auth/setting`)
      .then((res) => {
        setSetting({
          IGST: res.data.IGST ?? 0,
          CGST: res.data.CGST ?? 0,
          SGST: res.data.SGST ?? 0,
          InvoiceSN: res.data.InvoiceSN ?? '',
          logo: res.data.logo ?? null,
          selected_fys: res.data.selected_fys ?? null,
          sms_api_url: res.data.sms_api_url ?? '',
          sms_auth_key: res.data.sms_auth_key ?? '',
          sms_sender_id: res.data.sms_sender_id ?? '',
          smtp_host: res.data.smtp_host ?? '',
          smtp_port: res.data.smtp_port ?? null,
          smtp_user: res.data.smtp_user ?? '',
          smtp_pass: res.data.smtp_pass ?? '',
          smtp_secure: !!res.data.smtp_secure,
          smtp_from_email: res.data.smtp_from_email ?? '',
          wa_api_url: res.data.wa_api_url ?? '',
          wa_token: res.data.wa_token ?? '',
          wa_phone_number_id: res.data.wa_phone_number_id ?? '',
          wa_business_account_id: res.data.wa_business_account_id ?? '',
        });
        try {
          const parsed = JSON.parse(res.data.selected_fys || "[]");
          if (Array.isArray(parsed)) {
            setSelectedFys(parsed);
          } else {
            setSelectedFys([]);
          }
        } catch (e) {
          setSelectedFys([]);
        }
      })
      .catch(() => setError('Unable to load settings.'))
      .finally(() => setLoading(false));
  };

  const fetchUsers = () => {
    axios
      .get(`${API_URL}/auth/allusersinfo`)
      .then((res) => {
        setUsers(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        setUsers([]);
      });
  };

  const fetchCompanies = () => {
    axios
      .get(`${API_URL}/auth/companies`)
      .then((res) => {
        setCompanies(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        setCompanies([]);
      });
  };

  useEffect(() => {
    fetchSettings();
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (activeSubTab === 'users') {
      fetchUsers();
      fetchCompanies();
    }
  }, [activeSubTab]);

  // Handle input changes for form fields
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as any;
    setSetting((prev) => ({
      ...prev,
      [name]: type === 'checkbox'
        ? (e.target as any).checked
        : (name === 'IGST' || name === 'CGST' || name === 'SGST' || name === 'smtp_port')
          ? (value === '' ? null : Number(value))
          : value,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        ...setting,
        selected_fys: JSON.stringify(selectedFys),
      };
      await axios.put(`${API_URL}/auth/setting`, payload);
      setSuccess('Settings updated successfully!');
    } catch (err) {
      setError('Failed to update settings.');
    } finally {
      setSubmitting(false);
    }
  };
  // Upload logo
  const handleLogoUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!logoFile) return;
    setSubmitting(true);
    setError('');
    setSuccess('');
    
    const formData = new FormData();
    formData.append('logo', logoFile);

    try {
      const res = await axios.put(`${API_URL}/auth/setting/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSetting(prev => ({ ...prev, logo: res.data.logo }));
      setSuccess('Company logo uploaded successfully!');
      setLogoFile(null);
    } catch (err) {
      setError('Failed to upload logo.');
    } finally {
      setSubmitting(false);
    }
  };

  // Create user
  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUser.username || !newUser.email || !newUser.password) {
      setError('Please fill in all user details.');
      return;
    }
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await axios.post(`${API_URL}/auth/register`, {
        username: newUser.username,
        email: newUser.email,
        password: newUser.password,
        company_id: newUser.company_id || null,
        subscription_plan_id: newUser.subscription_plan_id || null,
        subscription_start_date: newUser.subscription_start_date || null,
        subscription_status: newUser.subscription_status || 'active'
      });
      setSuccess('User created successfully!');
      setNewUser({ username: '', email: '', password: '', company_id: '', subscription_plan_id: '', subscription_start_date: '', subscription_status: 'active' });
      setShowCreatePass(false);
      fetchUsers();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to create user.');
    } finally {
      setSubmitting(false);
    }
  };

  // Update user company mapping
  const handleUpdateUserCompany = async (userId: number, companyId: string) => {
    setError('');
    setSuccess('');
    try {
      await axios.put(`${API_URL}/auth/user/${userId}/company`, {
        company_id: companyId ? Number(companyId) : null
      });
      setSuccess('User company mapping updated successfully!');
      fetchUsers();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to update company mapping.');
    }
  };

  // Change user password
  const handleChangePassword = async (id: number) => {
    if (!newPasswordVal.trim()) {
      alert("Please enter a valid password.");
      return;
    }
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await axios.put(`${API_URL}/auth/user/${id}/password`, { password: newPasswordVal });
      setSuccess('Password updated successfully!');
      setEditingUserId(null);
      setNewPasswordVal('');
      setShowEditPass(false);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to update password.');
    } finally {
      setSubmitting(false);
    }
  };

  // Delete user
  const handleDeleteUser = async (id: number, username: string) => {
    if (username === 'admin') {
      alert("The primary 'admin' account cannot be deleted.");
      return;
    }
    if (!window.confirm(`Are you sure you want to delete user "${username}"?`)) return;
    setError('');
    setSuccess('');
    try {
      await axios.delete(`${API_URL}/auth/user/${id}`);
      setSuccess('User deleted successfully.');
      fetchUsers();
    } catch (err) {
      setError('Failed to delete user.');
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-10 px-4">
      {/* Header */}
      <div className="mb-8 text-center">
        <h2 className="text-3xl font-extrabold text-blue-900 tracking-tight">System Settings</h2>
        <p className="text-gray-500 mt-1">Configure global parameters, company branding, and manage team access.</p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-blue-100 mb-8 gap-2 justify-center">
        <button
          onClick={() => { setActiveSubTab('tax'); setError(''); setSuccess(''); }}
          className={`py-3 px-6 font-bold text-sm transition-all rounded-t-xl border-b-2 flex items-center gap-2 ${
            activeSubTab === 'tax'
              ? 'border-blue-600 text-blue-700 bg-blue-50/50'
              : 'border-transparent text-gray-500 hover:text-blue-600 hover:bg-gray-50/50'
          }`}
        >
          <FaCogs /> Tax & Invoice Config
        </button>
        <button
          onClick={() => { setActiveSubTab('logo'); setError(''); setSuccess(''); }}
          className={`py-3 px-6 font-bold text-sm transition-all rounded-t-xl border-b-2 flex items-center gap-2 ${
            activeSubTab === 'logo'
              ? 'border-blue-600 text-blue-700 bg-blue-50/50'
              : 'border-transparent text-gray-500 hover:text-blue-600 hover:bg-gray-50/50'
          }`}
        >
          <FaImage /> Company Logo
        </button>
        <button
          onClick={() => { setActiveSubTab('users'); setError(''); setSuccess(''); }}
          className={`py-3 px-6 font-bold text-sm transition-all rounded-t-xl border-b-2 flex items-center gap-2 ${
            activeSubTab === 'users'
              ? 'border-blue-600 text-blue-700 bg-blue-50/50'
              : 'border-transparent text-gray-500 hover:text-blue-600 hover:bg-gray-50/50'
          }`}
        >
          <FaUsers /> User Management
        </button>
        <button
          onClick={() => { setActiveSubTab('fy'); setError(''); setSuccess(''); }}
          className={`py-3 px-6 font-bold text-sm transition-all rounded-t-xl border-b-2 flex items-center gap-2 ${
            activeSubTab === 'fy'
              ? 'border-blue-600 text-blue-700 bg-blue-50/50'
              : 'border-transparent text-gray-500 hover:text-blue-600 hover:bg-gray-50/50'
          }`}
        >
          <FaCalendarAlt /> Financial Years
        </button>
        <button
          onClick={() => { setActiveSubTab('apis'); setError(''); setSuccess(''); }}
          className={`py-3 px-6 font-bold text-sm transition-all rounded-t-xl border-b-2 flex items-center gap-2 ${
            activeSubTab === 'apis'
              ? 'border-blue-600 text-blue-700 bg-blue-50/50'
              : 'border-transparent text-gray-500 hover:text-blue-600 hover:bg-gray-50/50'
          }`}
        >
          <FaPaperPlane /> API & SMTP Config
        </button>
        {userRole === 'super-admin' && (
          <button
            onClick={() => { setActiveSubTab('subscriptions'); setError(''); setSuccess(''); }}
            className={`py-3 px-6 font-bold text-sm transition-all rounded-t-xl border-b-2 flex items-center gap-2 ${
              activeSubTab === 'subscriptions'
                ? 'border-blue-600 text-blue-700 bg-blue-50/50'
                : 'border-transparent text-gray-500 hover:text-blue-600 hover:bg-gray-50/50'
            }`}
          >
            <FaKey /> Subscription Plans
          </button>
        )}
        <button
          onClick={() => { setActiveSubTab('security'); setError(''); setSuccess(''); }}
          className={`py-3 px-6 font-bold text-sm transition-all rounded-t-xl border-b-2 flex items-center gap-2 ${
            activeSubTab === 'security'
              ? 'border-blue-600 text-blue-700 bg-blue-50/50'
              : 'border-transparent text-gray-500 hover:text-blue-600 hover:bg-gray-50/50'
          }`}
        >
          <FaKey /> Change Password
        </button>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-xl border border-red-200 text-center font-semibold">{error}</div>}
      {success && <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-xl border border-green-200 text-center font-semibold">{success}</div>}

      <div className="bg-white rounded-3xl border border-blue-100 shadow-xl p-8">
        {loading ? (
          <div className="text-center py-10 text-gray-500 font-semibold">Loading settings...</div>
        ) : (
          <>
            {/* Tax Settings Tab */}
            {activeSubTab === 'tax' && (
              <form className="space-y-6" onSubmit={handleSubmit}>
                <h3 className="text-xl font-bold text-blue-900 border-b pb-2">Tax Percentage Rates</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs" htmlFor="IGST">
                      IGST (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      id="IGST"
                      name="IGST"
                      value={setting.IGST}
                      onChange={handleInputChange}
                      className="w-full border-2 border-blue-100 rounded-xl px-4 py-2 focus:border-blue-300 focus:outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs" htmlFor="CGST">
                      CGST (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      id="CGST"
                      name="CGST"
                      value={setting.CGST}
                      onChange={handleInputChange}
                      className="w-full border-2 border-blue-100 rounded-xl px-4 py-2 focus:border-blue-300 focus:outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs" htmlFor="SGST">
                      SGST (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      id="SGST"
                      name="SGST"
                      value={setting.SGST}
                      onChange={handleInputChange}
                      className="w-full border-2 border-blue-100 rounded-xl px-4 py-2 focus:border-blue-300 focus:outline-none"
                      required
                    />
                  </div>
                </div>

                <div className="pt-4">
                  <h3 className="text-xl font-bold text-blue-900 border-b pb-2">Invoice Configuration</h3>
                  <div className="mt-4">
                    <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs" htmlFor="InvoiceSN">
                      Invoice Serial Number (Prefix)
                    </label>
                    <input
                      type="text"
                      id="InvoiceSN"
                      name="InvoiceSN"
                      value={setting.InvoiceSN}
                      onChange={handleInputChange}
                      className="w-full border-2 border-blue-100 rounded-xl px-4 py-2 focus:border-blue-300 focus:outline-none max-w-md"
                      required
                      placeholder="e.g. ATS/"
                      maxLength={20}
                    />
                    <p className="text-xs text-gray-400 mt-1">This prefix is prepended to the generated sales invoice numbers.</p>
                  </div>
                </div>

                <div className="pt-6 flex justify-end">
                  <button
                    type="submit"
                    className="bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold px-6 py-2.5 rounded-xl hover:from-blue-500 hover:to-blue-400 shadow-md transition"
                    disabled={submitting}
                  >
                    {submitting ? 'Updating...' : 'Update Settings'}
                  </button>
                </div>
              </form>
            )}

            {/* Logo Settings Tab */}
            {activeSubTab === 'logo' && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-blue-900 border-b pb-2">Company Brand Logo</h3>
                
                <div className="flex flex-col md:flex-row items-center gap-8 py-4">
                  <div className="flex flex-col items-center">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Current Logo</span>
                    {setting.logo ? (
                      <div className="border border-blue-100 rounded-2xl p-4 bg-gray-50 flex items-center justify-center max-w-[200px] max-h-[160px] overflow-hidden shadow-inner">
                        <img 
                          src={setting.logo.startsWith('http') ? setting.logo : `${API_URL}${setting.logo}`} 
                          alt="Company Logo" 
                          className="max-h-28 object-contain"
                        />
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-200 rounded-2xl w-44 h-28 flex flex-col items-center justify-center text-gray-400 bg-gray-50 text-xs">
                        <span role="img" aria-label="image" className="text-xl mb-1">🖼️</span>
                        No logo uploaded
                      </div>
                    )}
                  </div>

                  <form className="flex-1 space-y-4" onSubmit={handleLogoUpload}>
                    <label className="block text-sm font-semibold text-gray-700">Choose a new image file (PNG/JPG preferred)</label>
                    <input 
                      type="file" 
                      accept="image/*"
                      onChange={e => setLogoFile(e.target.files ? e.target.files[0] : null)}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    <button
                      type="submit"
                      disabled={!logoFile || submitting}
                      className="px-6 py-2.5 bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-500 hover:to-emerald-400 text-white rounded-xl font-semibold shadow-md disabled:opacity-50 transition flex items-center gap-2"
                    >
                      <FaUpload /> {submitting ? 'Uploading...' : 'Upload Logo'}
                    </button>
                  </form>
                </div>
              </div>
            )}

            {/* User Management Tab */}
            {activeSubTab === 'users' && (
              <div className="space-y-8">
                {/* User registration form */}
                <div>
                  <h3 className="text-xl font-bold text-blue-900 border-b pb-2 mb-4 flex items-center gap-2">
                    <FaUserPlus className="text-blue-600" /> Create Authorized User
                  </h3>
                  <form onSubmit={handleCreateUser} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end bg-blue-50/50 p-6 rounded-2xl border border-blue-100">
                    <div>
                      <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs" htmlFor="username">
                        Username
                      </label>
                      <input
                        type="text"
                        id="username"
                        value={newUser.username}
                        onChange={e => setNewUser({ ...newUser, username: e.target.value })}
                        className="w-full border-2 border-white rounded-xl px-4 py-2 bg-white focus:border-blue-300 focus:outline-none text-sm font-semibold text-gray-800"
                        required
                        placeholder="Choose username"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs" htmlFor="email">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={newUser.email}
                        onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                        className="w-full border-2 border-white rounded-xl px-4 py-2 bg-white focus:border-blue-300 focus:outline-none text-sm font-semibold text-gray-800"
                        required
                        placeholder="e.g. user@company.com"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs" htmlFor="password">
                        Password
                      </label>
                      <div className="relative">
                        <input
                          type={showCreatePass ? "text" : "password"}
                          id="password"
                          value={newUser.password}
                          onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                          className="w-full border-2 border-white rounded-xl pl-4 pr-12 py-2 bg-white focus:border-blue-300 focus:outline-none text-sm"
                          required
                          placeholder="••••••••"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCreatePass(!showCreatePass)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-blue-600 hover:text-blue-800 uppercase tracking-wider focus:outline-none"
                        >
                          {showCreatePass ? "Hide" : "Show"}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs" htmlFor="company_id">
                        Mapped Company
                      </label>
                      <select
                        id="company_id"
                        value={newUser.company_id}
                        onChange={e => setNewUser({ ...newUser, company_id: e.target.value })}
                        className="w-full border-2 border-white rounded-xl px-4 py-2 bg-white focus:border-blue-300 focus:outline-none text-sm font-semibold text-gray-800"
                      >
                        <option value="">All Companies (Admin)</option>
                        {companies.map((c: any) => (
                          <option key={c.id} value={c.id}>
                            {c.company_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs" htmlFor="subscription_plan_id">
                        Subscription Plan
                      </label>
                      <select
                        id="subscription_plan_id"
                        value={newUser.subscription_plan_id}
                        onChange={e => setNewUser({ ...newUser, subscription_plan_id: e.target.value })}
                        className="w-full border-2 border-white rounded-xl px-4 py-2 bg-white focus:border-blue-300 focus:outline-none text-sm font-semibold text-gray-800"
                      >
                        <option value="">No Plan</option>
                        {subscriptionPlans.map((p: any) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs" htmlFor="subscription_start_date">
                        Start Date
                      </label>
                      <input
                        type="date"
                        id="subscription_start_date"
                        value={newUser.subscription_start_date}
                        onChange={e => setNewUser({ ...newUser, subscription_start_date: e.target.value })}
                        className="w-full border-2 border-white rounded-xl px-4 py-2 bg-white focus:border-blue-300 focus:outline-none text-sm font-semibold text-gray-800"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs" htmlFor="subscription_status">
                        Status
                      </label>
                      <select
                        id="subscription_status"
                        value={newUser.subscription_status}
                        onChange={e => setNewUser({ ...newUser, subscription_status: e.target.value })}
                        className="w-full border-2 border-white rounded-xl px-4 py-2 bg-white focus:border-blue-300 focus:outline-none text-sm font-semibold text-gray-800"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                    <div>
                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-2.5 rounded-xl shadow-md transition disabled:opacity-50 text-sm"
                      >
                        Create
                      </button>
                    </div>
                  </form>
                </div>

                {/* Users List */}
                <div>
                  <h3 className="text-xl font-bold text-blue-900 border-b pb-2 mb-4">Authorized User List</h3>
                  {users.length === 0 ? (
                    <div className="text-center py-4 bg-gray-50 border rounded-2xl text-gray-400 italic">No users found.</div>
                  ) : (
                    <div className="overflow-hidden border border-blue-50 rounded-2xl bg-white/50 animate-fade-in">
                      <table className="min-w-full divide-y divide-blue-50 text-left">
                        <thead className="bg-blue-50/40 text-xs font-bold text-blue-900/70 uppercase">
                          <tr>
                            <th className="p-4">Username</th>
                            <th className="p-4">Email</th>
                            <th className="p-4">Mapped Company</th>
                            <th className="p-4">Subscription</th>
                            <th className="p-4 text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-blue-50/30 text-sm">
                          {users.map(u => (
                            <tr key={u.id} className="hover:bg-blue-50/10 transition-colors">
                              <td className="p-4 font-bold text-gray-800">{u.username || u.name}</td>
                              <td className="p-4 text-gray-600">{u.email}</td>
                              <td className="p-4">
                                <select
                                  value={u.company_id || ""}
                                  onChange={(e) => handleUpdateUserCompany(u.id, e.target.value)}
                                  className="border border-blue-200 rounded-lg p-1.5 text-xs bg-white font-semibold text-gray-800 focus:border-blue-300 focus:outline-none"
                                >
                                  <option value="">All Companies (Admin)</option>
                                  {companies.map((c: any) => (
                                    <option key={c.id} value={c.id}>
                                      {c.company_name}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="p-4">
                                <div className="flex flex-col gap-1">
                                  {u.subscription_plan_name ? (
                                    <>
                                      <span className="font-bold text-indigo-950 text-xs">{u.subscription_plan_name}</span>
                                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold w-fit ${
                                        u.subscription_status === 'active' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'
                                      }`}>
                                        {u.subscription_status}
                                      </span>
                                    </>
                                  ) : (
                                    <span className="text-gray-400 text-xs italic">No Active Plan</span>
                                  )}
                                  {userRole === 'super-admin' && (
                                    <button
                                      onClick={() => {
                                        setSelectedUserForSub(u);
                                        setUserSubForm({
                                          subscription_plan_id: u.subscription_plan_id ? String(u.subscription_plan_id) : '',
                                          subscription_start_date: u.subscription_start_date ? u.subscription_start_date.substring(0, 10) : new Date().toISOString().substring(0, 10),
                                          subscription_status: u.subscription_status || 'active'
                                        });
                                        setShowUserSubModal(true);
                                      }}
                                      className="text-indigo-600 hover:text-indigo-800 text-[10px] font-black uppercase text-left w-fit mt-1 border-b border-indigo-600 border-dotted"
                                    >
                                      Assign
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="p-4">
                                {editingUserId === u.id ? (
                                  <div className="flex gap-2 items-center justify-center">
                                    <div className="relative">
                                      <input
                                        type={showEditPass ? "text" : "password"}
                                        value={newPasswordVal}
                                        onChange={e => setNewPasswordVal(e.target.value)}
                                        className="border border-blue-300 rounded-lg px-2 py-1 text-xs focus:outline-none"
                                        placeholder="New password"
                                        required
                                      />
                                      <button
                                        type="button"
                                        onClick={() => setShowEditPass(!showEditPass)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-black text-blue-600 uppercase"
                                      >
                                        {showEditPass ? "Hide" : "Show"}
                                      </button>
                                    </div>
                                    <button
                                      onClick={() => handleChangePassword(u.id)}
                                      className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded-lg text-[10px] font-bold uppercase transition"
                                      disabled={submitting}
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={() => { setEditingUserId(null); setNewPasswordVal(''); setShowEditPass(false); }}
                                      className="px-3 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-[10px] font-bold uppercase transition"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex gap-2 items-center justify-center">
                                    <button
                                      onClick={() => { setEditingUserId(u.id); setNewPasswordVal(''); setShowEditPass(false); }}
                                      className="text-blue-500 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-xl transition flex items-center gap-1.5 text-xs font-semibold shadow-sm"
                                      title="Change password"
                                    >
                                      <FaKey size={10} /> Change Password
                                    </button>
                                    <button
                                      onClick={() => handleDeleteUser(u.id, u.username || u.name)}
                                      disabled={u.username === 'admin' || u.name === 'admin'}
                                      className="text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 p-2 rounded-xl transition disabled:opacity-30"
                                      title={u.username === 'admin' ? "Cannot delete admin account" : "Delete user"}
                                    >
                                      <FaTrash size={12} />
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Financial Years Settings Tab */}
            {activeSubTab === 'fy' && (
              <form className="space-y-6" onSubmit={handleSubmit}>
                <h3 className="text-xl font-bold text-blue-900 border-b pb-2">Filter Data by Financial Years</h3>
                <p className="text-sm text-gray-500">
                  Select which financial years should be active in the system. The dashboard statistics, lists, and reports will only process data within the selected periods.
                </p>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 py-4">
                  {[
                    { label: "FY 2020-21", value: "20-21" },
                    { label: "FY 2021-22", value: "21-22" },
                    { label: "FY 2022-23", value: "22-23" },
                    { label: "FY 2023-24", value: "23-24" },
                    { label: "FY 2024-25", value: "24-25" },
                    { label: "FY 2025-26", value: "25-26" },
                    { label: "FY 2026-27", value: "26-27" },
                    { label: "FY 2027-28", value: "27-28" },
                    { label: "FY 2028-29", value: "28-29" },
                    { label: "FY 2029-30", value: "29-30" },
                    { label: "FY 2030-31", value: "30-31" },
                  ].map((fy) => {
                    const isChecked = selectedFys.includes(fy.value);
                    return (
                      <label
                        key={fy.value}
                        className={`flex items-center gap-3 p-4 rounded-2xl border-2 cursor-pointer select-none transition-all ${
                          isChecked
                            ? "border-blue-500 bg-blue-50/50 text-blue-900 font-bold shadow-sm"
                            : "border-gray-200 hover:border-blue-200 text-gray-600 hover:bg-gray-50/50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedFys([...selectedFys, fy.value]);
                            } else {
                              setSelectedFys(selectedFys.filter((v) => v !== fy.value));
                            }
                          }}
                          className="accent-blue-600 h-5 w-5"
                        />
                        <div className="flex flex-col">
                          <span>{fy.label}</span>
                          <span className="text-[10px] text-gray-400 font-normal">
                            1 Apr 20{fy.value.split('-')[0]} - 31 Mar 20{fy.value.split('-')[1]}
                          </span>
                        </div>
                      </label>
                    );
                  })}
                </div>

                <div className="pt-6 flex justify-end">
                  <button
                    type="submit"
                    className="bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold px-6 py-2.5 rounded-xl hover:from-blue-500 hover:to-blue-400 shadow-md transition"
                    disabled={submitting}
                  >
                    {submitting ? 'Updating...' : 'Save Financial Year Settings'}
                  </button>
                </div>
              </form>
            )}

            {/* API & SMTP Configurations Tab */}
            {activeSubTab === 'apis' && (
              <form className="space-y-8" onSubmit={handleSubmit}>
                {/* Email SMTP configurations */}
                <div>
                  <h3 className="text-xl font-bold text-blue-900 border-b pb-2 mb-4 flex items-center gap-2">
                    ✉️ Email SMTP Server Settings
                  </h3>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs">
                        SMTP Host
                      </label>
                      <input
                        type="text"
                        name="smtp_host"
                        value={setting.smtp_host || ''}
                        onChange={handleInputChange}
                        className="w-full border-2 border-blue-100 rounded-xl px-4 py-2 focus:border-blue-300 focus:outline-none text-sm"
                        placeholder="e.g. smtp.gmail.com"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs">
                        SMTP Port
                      </label>
                      <input
                        type="number"
                        name="smtp_port"
                        value={setting.smtp_port === null ? '' : setting.smtp_port}
                        onChange={handleInputChange}
                        className="w-full border-2 border-blue-100 rounded-xl px-4 py-2 focus:border-blue-300 focus:outline-none text-sm"
                        placeholder="e.g. 587 or 465"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs">
                        From Email (Sender)
                      </label>
                      <input
                        type="email"
                        name="smtp_from_email"
                        value={setting.smtp_from_email || ''}
                        onChange={handleInputChange}
                        className="w-full border-2 border-blue-100 rounded-xl px-4 py-2 focus:border-blue-300 focus:outline-none text-sm"
                        placeholder="e.g. billing@mycompany.com"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs">
                        SMTP Username
                      </label>
                      <input
                        type="text"
                        name="smtp_user"
                        value={setting.smtp_user || ''}
                        onChange={handleInputChange}
                        className="w-full border-2 border-blue-100 rounded-xl px-4 py-2 focus:border-blue-300 focus:outline-none text-sm"
                        placeholder="e.g. username/email"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs">
                        SMTP Password
                      </label>
                      <input
                        type="password"
                        name="smtp_pass"
                        value={setting.smtp_pass || ''}
                        onChange={handleInputChange}
                        className="w-full border-2 border-blue-100 rounded-xl px-4 py-2 focus:border-blue-300 focus:outline-none text-sm"
                        placeholder="••••••••"
                      />
                    </div>
                    <div className="flex items-center pt-6">
                      <label className="flex items-center gap-2 font-semibold text-blue-900/80 text-sm cursor-pointer select-none">
                        <input
                          type="checkbox"
                          name="smtp_secure"
                          checked={!!setting.smtp_secure}
                          onChange={handleInputChange}
                          className="accent-blue-600 h-5 w-5"
                        />
                        Use SSL/TLS (Secure Connection)
                      </label>
                    </div>
                  </div>
                </div>

                {/* SMS API settings */}
                <div className="pt-4 border-t border-gray-100">
                  <h3 className="text-xl font-bold text-blue-900 pb-2 mb-2 flex items-center gap-2">
                    💬 SMS API Configuration
                  </h3>
                  <p className="text-xs text-gray-500 mb-4">
                    Configure your custom SMS HTTP gateway. Use dynamic placeholders in the URL: <code className="bg-gray-100 px-1 py-0.5 rounded font-mono text-[10px] text-blue-700">&#123;&#123;auth_key&#125;&#125;</code>, <code className="bg-gray-100 px-1 py-0.5 rounded font-mono text-[10px] text-blue-700">&#123;&#123;sender_id&#125;&#125;</code>, <code className="bg-gray-100 px-1 py-0.5 rounded font-mono text-[10px] text-blue-700">&#123;&#123;to&#125;&#125;</code>, and <code className="bg-gray-100 px-1 py-0.5 rounded font-mono text-[10px] text-blue-700">&#123;&#123;message&#125;&#125;</code>.
                  </p>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs">
                        SMS API URL (GET Endpoint)
                      </label>
                      <input
                        type="text"
                        name="sms_api_url"
                        value={setting.sms_api_url || ''}
                        onChange={handleInputChange}
                        className="w-full border-2 border-blue-100 rounded-xl px-4 py-2 focus:border-blue-300 focus:outline-none text-sm font-mono"
                        placeholder="e.g. https://api.smsprovider.com/send?apikey={{auth_key}}&sender={{sender_id}}&mobile={{to}}&message={{message}}"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs">
                        Auth Key
                      </label>
                      <input
                        type="text"
                        name="sms_auth_key"
                        value={setting.sms_auth_key || ''}
                        onChange={handleInputChange}
                        className="w-full border-2 border-blue-100 rounded-xl px-4 py-2 focus:border-blue-300 focus:outline-none text-sm"
                        placeholder="Gateway authentication/license key"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs">
                        Sender ID
                      </label>
                      <input
                        type="text"
                        name="sms_sender_id"
                        value={setting.sms_sender_id || ''}
                        onChange={handleInputChange}
                        className="w-full border-2 border-blue-100 rounded-xl px-4 py-2 focus:border-blue-300 focus:outline-none text-sm"
                        placeholder="e.g. MYCOMP"
                      />
                    </div>
                  </div>
                </div>

                {/* WhatsApp Business API settings */}
                <div className="pt-4 border-t border-gray-100">
                  <h3 className="text-xl font-bold text-blue-900 pb-2 mb-2 flex items-center gap-2">
                    🟢 WhatsApp Business API Details
                  </h3>
                  <p className="text-xs text-gray-500 mb-4">
                    Configure standard Meta Cloud API or a custom gateway. For Meta, fill in the Access Token and Phone Number ID.
                  </p>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs">
                        WhatsApp Custom Gateway URL (Optional)
                      </label>
                      <input
                        type="text"
                        name="wa_api_url"
                        value={setting.wa_api_url || ''}
                        onChange={handleInputChange}
                        className="w-full border-2 border-blue-100 rounded-xl px-4 py-2 focus:border-blue-300 focus:outline-none text-sm font-mono"
                        placeholder="Leave blank to use default Meta Cloud API, or enter custom URL"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs">
                        Phone Number ID (Meta)
                      </label>
                      <input
                        type="text"
                        name="wa_phone_number_id"
                        value={setting.wa_phone_number_id || ''}
                        onChange={handleInputChange}
                        className="w-full border-2 border-blue-100 rounded-xl px-4 py-2 focus:border-blue-300 focus:outline-none text-sm"
                        placeholder="e.g. 104729184710482"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs">
                        WhatsApp Business Account ID (Meta)
                      </label>
                      <input
                        type="text"
                        name="wa_business_account_id"
                        value={setting.wa_business_account_id || ''}
                        onChange={handleInputChange}
                        className="w-full border-2 border-blue-100 rounded-xl px-4 py-2 focus:border-blue-300 focus:outline-none text-sm"
                        placeholder="e.g. 984029472910"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs">
                        Permanent Access Token / Bearer Token
                      </label>
                      <textarea
                        name="wa_token"
                        value={setting.wa_token || ''}
                        onChange={handleInputChange as any}
                        rows={2}
                        className="w-full border-2 border-blue-100 rounded-xl px-4 py-2 focus:border-blue-300 focus:outline-none text-sm font-mono"
                        placeholder="EAABw..."
                      />
                    </div>
                  </div>
                </div>

                {/* Gemini AI OCR settings */}
                <div className="pt-4 border-t border-gray-100">
                  <h3 className="text-xl font-bold text-blue-900 pb-2 mb-2 flex items-center gap-2">
                    🤖 Google Gemini AI OCR Details
                  </h3>
                  <p className="text-xs text-gray-500 mb-4">
                    Set up your Google Gemini API key to enable high-accuracy, zero-click parsing of purchase invoices. Gemini 1.5/2.5 Flash is used for the free tier.
                  </p>
                  <div className="grid md:grid-cols-1 gap-6">
                    <div>
                      <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs">
                        Gemini API Key
                      </label>
                      <input
                        type="password"
                        name="gemini_api_key"
                        value={setting.gemini_api_key || ''}
                        onChange={handleInputChange}
                        className="w-full border-2 border-blue-100 rounded-xl px-4 py-2 focus:border-blue-300 focus:outline-none text-sm font-mono"
                        placeholder="AIzaSy..."
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-6 flex justify-end">
                  <button
                    type="submit"
                    className="bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold px-6 py-2.5 rounded-xl hover:from-blue-500 hover:to-blue-400 shadow-md transition"
                    disabled={submitting}
                  >
                    {submitting ? 'Updating...' : 'Save API & SMTP Settings'}
                  </button>
                </div>
              </form>
            )}

            {activeSubTab === 'subscriptions' && userRole === 'super-admin' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between border-b border-gray-100 pb-4">
                  <div>
                    <h3 className="text-xl font-bold text-blue-900">Subscription Plans</h3>
                    <p className="text-xs text-gray-500 mt-1">Manage billing plans and account usage limits.</p>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedPlan(null);
                      setPlanForm({ name: '', price: '', duration_months: '1', max_invoices: '100', max_users: '10' });
                      setShowPlanModal(true);
                    }}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs px-4 py-2 rounded-xl shadow transition flex items-center gap-1"
                  >
                    + Create Plan
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-blue-50/50 text-blue-950 text-[11px] font-extrabold uppercase tracking-wider">
                        <th className="border border-blue-100/50 px-4 py-3 text-left">Plan Name</th>
                        <th className="border border-blue-100/50 px-4 py-3 text-right">Price (₹)</th>
                        <th className="border border-blue-100/50 px-4 py-3 text-center">Duration (Months)</th>
                        <th className="border border-blue-100/50 px-4 py-3 text-center">Max Invoices/Month</th>
                        <th className="border border-blue-100/50 px-4 py-3 text-center">Max Users</th>
                        <th className="border border-blue-100/50 px-4 py-3 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptionPlans.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center py-10 text-slate-400 font-medium text-sm">
                            No plans configured. Click "Create Plan" to define tiers.
                          </td>
                        </tr>
                      ) : (
                        subscriptionPlans.map((plan) => (
                          <tr key={plan.id} className="text-slate-700 font-medium text-sm hover:bg-slate-50/50 transition">
                            <td className="border border-slate-100 px-4 py-3.5 font-bold text-indigo-950">{plan.name}</td>
                            <td className="border border-slate-100 px-4 py-3.5 text-right font-mono font-bold text-slate-900">₹{parseFloat(plan.price).toFixed(2)}</td>
                            <td className="border border-slate-100 px-4 py-3.5 text-center">{plan.duration_months} m</td>
                            <td className="border border-slate-100 px-4 py-3.5 text-center">{plan.max_invoices >= 999999 ? 'Unlimited' : plan.max_invoices}</td>
                            <td className="border border-slate-100 px-4 py-3.5 text-center">{plan.max_users}</td>
                            <td className="border border-slate-100 px-4 py-3.5 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() => {
                                    setSelectedPlan(plan);
                                    setPlanForm({
                                      name: plan.name,
                                      price: String(plan.price),
                                      duration_months: String(plan.duration_months),
                                      max_invoices: String(plan.max_invoices),
                                      max_users: String(plan.max_users)
                                    });
                                    setShowPlanModal(true);
                                  }}
                                  className="text-xs font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1 rounded"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={async () => {
                                    if (window.confirm(`Are you sure you want to delete plan "${plan.name}"?`)) {
                                      try {
                                        await axios.delete(`${API_URL}/auth/subscription-plans/${plan.id}`);
                                        setSuccess(`Plan "${plan.name}" deleted successfully.`);
                                        fetchSubscriptionPlans();
                                      } catch (err: any) {
                                        setError(err.response?.data?.error || "Failed to delete subscription plan.");
                                      }
                                    }
                                  }}
                                  className="text-xs font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 px-2.5 py-1 rounded"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Plan Creation/Editing Modal */}
                {showPlanModal && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 animate__animated animate__fadeIn">
                    <div className="bg-white rounded-2xl p-6 max-w-md w-full border border-slate-200 shadow-xl relative">
                      <button
                        onClick={() => setShowPlanModal(false)}
                        className="absolute right-4 top-4 text-slate-400 hover:text-slate-600 text-lg font-bold"
                        style={{ background: 'none', border: 'none' }}
                      >
                        &times;
                      </button>
                      <h3 className="font-extrabold text-lg text-indigo-950 mb-4 flex items-center gap-2">
                        <FaCogs className="text-indigo-600" />
                        {selectedPlan ? "Edit Subscription Plan" : "Create Subscription Plan"}
                      </h3>
                      <form
                        onSubmit={async (e) => {
                          e.preventDefault();
                          setError('');
                          setSuccess('');
                          try {
                            if (selectedPlan) {
                              await axios.put(`${API_URL}/auth/subscription-plans/${selectedPlan.id}`, planForm);
                              setSuccess("Subscription plan updated successfully.");
                            } else {
                              await axios.post(`${API_URL}/auth/subscription-plans`, planForm);
                              setSuccess("Subscription plan created successfully.");
                            }
                            setShowPlanModal(false);
                            fetchSubscriptionPlans();
                          } catch (err: any) {
                            setError(err.response?.data?.error || "Failed to save subscription plan.");
                          }
                        }}
                        className="space-y-4"
                      >
                        <div className="flex flex-col gap-1.5">
                          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Plan Name *</label>
                          <input
                            type="text"
                            value={planForm.name}
                            onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                            required
                            placeholder="e.g. Starter Plan"
                            className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-slate-800"
                          />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Price (₹) *</label>
                          <input
                            type="number"
                            step="0.01"
                            value={planForm.price}
                            onChange={(e) => setPlanForm({ ...planForm, price: e.target.value })}
                            required
                            placeholder="e.g. 999.00"
                            className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-slate-800"
                          />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Duration (Months) *</label>
                          <input
                            type="number"
                            value={planForm.duration_months}
                            onChange={(e) => setPlanForm({ ...planForm, duration_months: e.target.value })}
                            required
                            placeholder="e.g. 1"
                            className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-slate-800"
                          />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Max Invoices/Month *</label>
                          <input
                            type="number"
                            value={planForm.max_invoices}
                            onChange={(e) => setPlanForm({ ...planForm, max_invoices: e.target.value })}
                            required
                            placeholder="e.g. 100"
                            className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-slate-800"
                          />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Max Users *</label>
                          <input
                            type="number"
                            value={planForm.max_users}
                            onChange={(e) => setPlanForm({ ...planForm, max_users: e.target.value })}
                            required
                            placeholder="e.g. 10"
                            className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-slate-800"
                          />
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                          <button
                            type="button"
                            onClick={() => setShowPlanModal(false)}
                            className="px-4 py-2 border rounded-xl text-gray-500 bg-gray-50 hover:bg-gray-100 text-xs font-bold"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-xs font-bold"
                          >
                            Save Plan
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            )}
            {activeSubTab === 'security' && (
              <div className="space-y-6 max-w-md mx-auto bg-white p-6 rounded-3xl border border-blue-50 shadow-md">
                <div className="border-b border-gray-100 pb-4">
                  <h3 className="text-xl font-bold text-blue-900 flex items-center gap-2">
                    <FaKey /> Change Password
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">Update your account login password safely.</p>
                </div>

                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    setError('');
                    setSuccess('');
                    if (!loggedUserId) {
                      setError('Failed to resolve logged-in user ID.');
                      return;
                    }
                    if (newSecPassword !== confirmSecPassword) {
                      setError('New password and confirmation do not match.');
                      return;
                    }
                    if (newSecPassword.length < 4) {
                      setError('Password must be at least 4 characters long.');
                      return;
                    }
                    setSubmitting(true);
                    try {
                      await axios.put(`${API_URL}/auth/user/${loggedUserId}/password`, { password: newSecPassword });
                      setSuccess('Password updated successfully!');
                      setNewSecPassword('');
                      setConfirmSecPassword('');
                    } catch (err: any) {
                      setError(err?.response?.data?.error || 'Failed to update password.');
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                  className="space-y-4 text-left"
                >
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">New Password</label>
                    <input
                      type={showSecPassword ? "text" : "password"}
                      value={newSecPassword}
                      onChange={(e) => setNewSecPassword(e.target.value)}
                      required
                      placeholder="Enter new password"
                      className="px-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-slate-800"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Confirm New Password</label>
                    <input
                      type={showSecPassword ? "text" : "password"}
                      value={confirmSecPassword}
                      onChange={(e) => setConfirmSecPassword(e.target.value)}
                      required
                      placeholder="Re-enter new password"
                      className="px-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-slate-800"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="showSecPassword"
                      checked={showSecPassword}
                      onChange={() => setShowSecPassword(!showSecPassword)}
                      className="rounded text-indigo-600 focus:ring-indigo-500/20"
                    />
                    <label htmlFor="showSecPassword" className="text-xs font-bold text-slate-500 uppercase cursor-pointer select-none">Show passwords</label>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 rounded-xl text-xs uppercase shadow transition disabled:opacity-50"
                  >
                    {submitting ? 'Updating...' : 'Update Password'}
                  </button>
                </form>
              </div>
            )}
          </>
        )}
      </div>
      {/* User Subscription Edit Modal */}
      {showUserSubModal && selectedUserForSub && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 animate__animated animate__fadeIn">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full border border-slate-200 shadow-xl relative">
            <button
              onClick={() => setShowUserSubModal(false)}
              className="absolute right-4 top-4 text-slate-400 hover:text-slate-600 text-lg font-bold"
              style={{ background: 'none', border: 'none' }}
            >
              &times;
            </button>
            <h3 className="font-extrabold text-lg text-indigo-950 mb-4">
              Manage User Subscription: {selectedUserForSub.username || selectedUserForSub.name}
            </h3>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setError('');
                setSuccess('');
                try {
                  await axios.put(`${API_URL}/auth/user/${selectedUserForSub.id}/subscription`, userSubForm);
                  setShowUserSubModal(false);
                  
                  // Refresh users list
                  const ures = await axios.get(`${API_URL}/auth/allusersinfo`);
                  setUsers(Array.isArray(ures.data) ? ures.data : []);
                  setSuccess("User subscription updated successfully.");
                } catch (err: any) {
                  setError(err.response?.data?.error || "Failed to update user subscription.");
                }
              }}
              className="space-y-4"
            >
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Plan Tier</label>
                <select
                  value={userSubForm.subscription_plan_id}
                  onChange={(e) => setUserSubForm({ ...userSubForm, subscription_plan_id: e.target.value })}
                  className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-slate-800"
                >
                  <option value="">No Active Plan</option>
                  {subscriptionPlans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} (₹{p.price} / {p.duration_months}m)
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Start Date</label>
                <input
                  type="date"
                  value={userSubForm.subscription_start_date}
                  onChange={(e) => setUserSubForm({ ...userSubForm, subscription_start_date: e.target.value })}
                  className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-slate-800"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Status</label>
                <select
                  value={userSubForm.subscription_status}
                  onChange={(e) => setUserSubForm({ ...userSubForm, subscription_status: e.target.value })}
                  className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-slate-800"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="expired">Expired</option>
                </select>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowUserSubModal(false)}
                  className="px-4 py-2 border rounded-xl text-gray-500 bg-gray-50 hover:bg-gray-100 text-xs font-bold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-md transition"
                >
                  Save Subscription
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
export default Settings;